import { useRef, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Cloud, CheckCircle2, Save, RefreshCw, Unplug, FolderOpen, Copy,
  Download, CloudUpload, HardDriveDownload,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/admin/Fields";
import CardCollapse from "./CardCollapse";

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, x = Number(n);
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 ? 2 : 1)} ${u[i]}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

/**
 * Standalone card for the Google Drive backup integration.
 * Lives next to the generic "Site Yedekleme" card so admins see Drive setup,
 * folder details, test connection, and a "Backup now (test)" button in one place.
 */
export default function DriveBackupCard() {
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["admin", "backup", "status"],
    queryFn: () => api.get("/admin/backup/status").then((r) => r.data),
    refetchInterval: 15000,
  });

  const [saJson, setSaJson] = useState("");
  const [folderId, setFolderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [runningTest, setRunningTest] = useState(false);
  const [testInfo, setTestInfo] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (status) setFolderId(status.folder_id || "");
  }, [status?.folder_id]);

  const chip = (
    <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${status?.has_credentials ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} data-testid="drive-chip">
      {status?.has_credentials ? "Bağlı" : "Bağlı değil"}
    </span>
  );

  const onPickJsonFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024) { toast.error("Dosya 50 KB'den büyük görünüyor, service account JSON'u değil."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setSaJson(String(ev.target.result || ""));
    reader.readAsText(file);
  };

  const saveCreds = async () => {
    if (!saJson.trim()) { toast.error("Önce JSON anahtarını yapıştırın veya dosyadan yükleyin."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/backup/credentials", {
        sa_json: saJson.trim(),
        folder_id: folderId.trim() || null,
      });
      toast.success(`Bağlandı: ${data.sa_email}`);
      setSaJson("");
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const saveFolder = async () => {
    setSaving(true);
    try {
      await api.put("/admin/backup/config", { folder_id: folderId.trim() });
      toast.success(folderId.trim() ? "Klasör ID kaydedildi" : "Klasör ID temizlendi");
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/backup/test");
      setTestInfo(data);
      toast.success(data.folder_name ? `✓ Bağlantı başarılı — klasör: ${data.folder_name}` : "✓ Drive bağlantısı başarılı");
    } catch (e) { setTestInfo(null); toast.error(errMsg(e)); }
    setTesting(false);
  };

  const runTestBackup = async () => {
    if (runningTest) return;
    if (!confirm("Hemen tam yedek üretilip Drive'a yüklenecek. Devam edilsin mi?")) return;
    setRunningTest(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      const driveOk = data.last_backup_drive === "sent";
      const sizeMb = (data.last_backup_size / 1024 / 1024).toFixed(2);
      if (driveOk) {
        toast.success(`✓ Drive'a yüklendi · ${sizeMb} MB`);
      } else if (data.last_backup_drive === "failed") {
        toast.error(`Drive yüklemesi başarısız: ${data.last_backup_drive_error || "bilinmeyen hata"}`);
      } else {
        toast(`Yedek alındı (${sizeMb} MB) fakat Drive'a yüklenmedi — bağlantı kurulu değil veya atlandı.`);
      }
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
      // auto-refresh folder details so the admin can see new ZIP appear
      if (status?.has_credentials && status?.folder_id) {
        await testConn();
      }
    } catch (e) { toast.error(errMsg(e)); }
    setRunningTest(false);
  };

  const disconnect = async () => {
    if (!confirm("Drive bağlantısı kesilecek. JSON ve klasör ID silinecek. Onaylıyor musunuz?")) return;
    setDisconnecting(true);
    try {
      await api.post("/admin/backup/disconnect");
      toast.success("Drive bağlantısı kesildi");
      setSaJson("");
      setFolderId("");
      setTestInfo(null);
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setDisconnecting(false);
  };

  const copySaEmail = async () => {
    if (!status?.sa_email) return;
    try { await navigator.clipboard.writeText(status.sa_email); toast.success("E-posta kopyalandı"); } catch {}
  };

  if (!status) return <div className="bg-white border rounded-xl p-5 text-sm text-stone-400">Drive durumu yükleniyor…</div>;

  return (
    <CardCollapse
      testid="drive-manager"
      icon={<Cloud size={16} />}
      title="Google Drive Yedekleme"
      description="Tam site yedeği (veritabanı + medya) her gece kendi Google Drive hesabınızın bir klasörüne otomatik yüklensin."
      chip={chip}
    >
      {/* Kurulum rehberi */}
      {!status.has_credentials && (
        <details className="group bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden" data-testid="drive-howto" open>
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between gap-3 select-none">
            <span className="font-semibold text-sm text-indigo-900 flex items-center gap-2">
              <HardDriveDownload size={16} />
              Kurulum Rehberi · 5 dakikada tamam
            </span>
            <span className="text-xs text-indigo-700 group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="px-4 pb-4 pt-1 text-xs text-stone-800 space-y-5">
            <p className="leading-relaxed">
              Aşağıdaki 4 adımı tamamladığınızda sitedeki her şey (tüm veriler + fotoğraflar + medya) her gece otomatik olarak kendi Google Drive hesabınızın bir klasörüne yüklenir. Yalnızca ilk kurulumda yapılır.
            </p>

            <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">1</span>
                <p className="font-semibold text-stone-900 text-[13px]">Google Cloud'da bir Service Account oluşturun</p>
              </div>
              <ol className="list-decimal pl-7 space-y-1 leading-relaxed">
                <li><a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">console.cloud.google.com</a> adresine gidin ve Google hesabınızla giriş yapın.</li>
                <li>Üst menüden <strong>proje seçici</strong> → <strong>"NEW PROJECT"</strong>. Örn. ad: <code className="bg-stone-100 border rounded px-1">olgu-yedek</code>.</li>
                <li>Sol menü: <strong>APIs &amp; Services → Library</strong> → <code className="bg-stone-100 border rounded px-1">Google Drive API</code> arayın → <strong>ENABLE</strong>.</li>
                <li>Sol menü: <strong>IAM &amp; Admin → Service Accounts → + CREATE SERVICE ACCOUNT</strong>. Ad: <code className="bg-stone-100 border rounded px-1">site-backup</code> → <strong>CREATE AND CONTINUE</strong> → opsiyonel adımları <strong>DONE</strong> ile geçin.</li>
                <li>Listede hesap adına tıklayın → üstte <strong>KEYS</strong> → <strong>ADD KEY → Create new key → JSON → CREATE</strong>. Dosya tarayıcınıza iner.</li>
              </ol>
              <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1">
                ⚠ İnen JSON <strong>özel anahtardır</strong>, kimseyle paylaşmayın.
              </p>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">2</span>
                <p className="font-semibold text-stone-900 text-[13px]">JSON'u aşağıdaki kutuya yükleyin</p>
              </div>
              <ol className="list-decimal pl-7 space-y-1 leading-relaxed">
                <li><strong>"Service Account JSON"</strong> alanına yapıştırın veya <strong>"Dosyadan yükle"</strong> ile seçin.</li>
                <li><strong>"Bağlan"</strong> butonuna basın. Üstteki durum <span className="text-emerald-700 font-medium">"Bağlı"</span> olacak ve SA e-postasını gösterecek.</li>
                <li>SA e-postasının yanındaki <strong>kopyala</strong> ikonuna basıp panoya alın (3. adımda kullanacağız).</li>
              </ol>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">3</span>
                <p className="font-semibold text-stone-900 text-[13px]">Drive'da klasör açıp SA ile paylaşın</p>
              </div>
              <ol className="list-decimal pl-7 space-y-1 leading-relaxed">
                <li><a href="https://drive.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">drive.google.com</a> → sol üstteki <strong>"+ Yeni → Klasör"</strong>. Ad: <code className="bg-stone-100 border rounded px-1">Olgu Site Yedekleri</code>.</li>
                <li>Klasöre <strong>sağ tık → "Paylaş"</strong>. Kopyaladığınız SA e-postasını yapıştırın.</li>
                <li>Yetki açılırını <strong>"Editör"</strong> seçin, "Bildirim gönder"i kapatın → <strong>"Gönder"</strong>.</li>
              </ol>
              <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1">
                ⚠ "Editör" yetkisi şart. "Görüntüleyen" yetki yetmez.
              </p>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">4</span>
                <p className="font-semibold text-stone-900 text-[13px]">Klasör ID'sini bulup aşağıya girin</p>
              </div>
              <p className="pl-7 leading-relaxed">
                Klasörün ID'si, URL'inde <code className="bg-stone-100 border rounded px-1">folders/</code> ifadesinden sonraki uzun karakter dizisidir.
              </p>
              <div className="pl-7 space-y-1.5">
                <p className="text-[11px] text-stone-600">Örnek:</p>
                <div className="bg-stone-900 text-stone-100 rounded p-2 font-mono text-[10.5px] break-all leading-relaxed">
                  https://drive.google.com/drive/folders/<span className="bg-emerald-500/30 text-emerald-200 px-1 rounded">1A2B3c4D5e6F7g8H9iJkLmNoPqRsTuVwX</span>?usp=sharing
                </div>
                <p className="text-[11px] text-stone-600">
                  <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 rounded px-1">↑ Yeşil kısım</span> klasör ID'nizdir.
                </p>
              </div>
              <ol className="list-decimal pl-7 space-y-1 leading-relaxed">
                <li>Drive'da az önce açtığınız klasöre çift tıklayarak içine girin.</li>
                <li>URL'den <code className="bg-stone-100 border rounded px-1">folders/</code>'tan sonraki uzun kısmı seçip <strong>kopyalayın</strong>.</li>
                <li>Veya klasöre <strong>sağ tık → "Bağlantıyı al"</strong>, linkten alın.</li>
                <li>Aşağıdaki <strong>"Drive klasör ID'si"</strong> alanına yapıştırın → <strong>"Kaydet"</strong>.</li>
                <li>Sağ taraftaki <strong>"Test et"</strong> butonu "✓ Bağlantı başarılı..." derse kurulum tamam.</li>
              </ol>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[12px]">
              <p className="font-semibold text-emerald-900 mb-1">🎉 Kurulumdan sonra</p>
              <p className="leading-relaxed text-emerald-900">
                Sol taraftaki "Site Yedekleme" kartından <strong>Periyot: "Günlük"</strong> ve <strong>Saklama: "Son 30 yedek"</strong> seçin. Her gece saat 03:00'te sitenin tüm verileri + medyası Drive'a yüklenir, 30 günden eski yedekler otomatik silinir.
              </p>
            </div>

            <p className="text-[11px] text-stone-500 italic">
              En sık unutulanlar: Drive klasörüne SA e-postasını <strong>Editör</strong> olarak eklemek ve Drive API'yi aktifleştirmek.
            </p>
          </div>
        </details>
      )}

      {/* Service Account JSON input */}
      {!status.has_credentials ? (
        <Field label="Service Account JSON" hint="JSON içeriğini doğrudan yapıştırın veya .json dosyasından yükleyin.">
          <div className="space-y-2">
            <textarea
              value={saJson}
              onChange={(e) => setSaJson(e.target.value)}
              placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN PRIVATE KEY-----...","client_email":"backup@your-project.iam.gserviceaccount.com", ...}'
              rows={5}
              spellCheck={false}
              className="w-full border rounded-md p-2 text-xs font-mono bg-white resize-y min-h-[120px]"
              data-testid="drive-sa-json"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" accept=".json,application/json" ref={fileRef} onChange={onPickJsonFile} className="hidden" data-testid="drive-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="drive-file-pick"><FolderOpen size={13} className="mr-1" />Dosyadan yükle</Button>
              <Button type="button" size="sm" onClick={saveCreds} disabled={saving || !saJson.trim()} data-testid="drive-save-creds">
                {saving ? <RefreshCw size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />}
                Bağlan
              </Button>
            </div>
          </div>
        </Field>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="drive-connected">
          <div className="border rounded-lg p-3 bg-emerald-50/40 border-emerald-200 flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-emerald-800">Service Account bağlı</p>
              <div className="flex items-center gap-1 mt-0.5 min-w-0">
                <code className="text-[11px] bg-white border rounded px-1 truncate flex-1" title={status.sa_email}>{status.sa_email}</code>
                <button type="button" onClick={copySaEmail} className="text-stone-500 hover:text-stone-800 shrink-0" title="Kopyala" data-testid="drive-copy-email"><Copy size={12} /></button>
              </div>
              {status.sa_project && <p className="text-[11px] text-stone-600 mt-0.5">Proje: {status.sa_project}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={testConn} disabled={testing} className="flex-1" data-testid="drive-test">
              {testing ? <RefreshCw size={13} className="mr-1 animate-spin" /> : <CheckCircle2 size={13} className="mr-1" />}Test et
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={disconnect} disabled={disconnecting} className="flex-1 text-red-700 hover:text-red-800" data-testid="drive-disconnect">
              {disconnecting ? <RefreshCw size={13} className="mr-1 animate-spin" /> : <Unplug size={13} className="mr-1" />}Bağlantıyı kes
            </Button>
          </div>
        </div>
      )}

      {/* Folder ID */}
      <Field label="Drive klasör ID'si" hint="Yedek dosyaların yazılacağı klasörün ID'si. Klasörü yukarıdaki SA e-postasıyla Editor olarak paylaşmayı unutmayın.">
        <div className="flex gap-2">
          <Input
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
            className="h-9 text-sm font-mono"
            data-testid="drive-folder-id"
          />
          <Button type="button" variant="outline" size="sm" onClick={saveFolder} disabled={saving || folderId === (status.folder_id || "")} data-testid="drive-save-folder">
            <Save size={13} className="mr-1" />Kaydet
          </Button>
        </div>
        {status.folder_id && status.folder_id === folderId && (
          <p className="text-[11px] text-emerald-700 mt-1">✓ Kayıtlı · Klasör: <strong>{status.folder_name}</strong></p>
        )}
      </Field>

      {/* Backup now (test) */}
      {status.has_credentials && status.folder_id && (
        <div className="border rounded-lg p-3 bg-stone-50 flex items-center justify-between gap-3 flex-wrap" data-testid="drive-run-row">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-900">Test için şimdi yedekle</p>
            <p className="text-[11px] text-stone-600 mt-0.5">Tam site yedeği (veritabanı + medya) hazırlanıp Drive klasörüne yüklenir. Zamanlanmış yedek dışında elle çalıştırmak için.</p>
          </div>
          <Button type="button" onClick={runTestBackup} disabled={runningTest} data-testid="drive-run-now">
            {runningTest ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <CloudUpload size={14} className="mr-1" />}
            {runningTest ? "Yükleniyor…" : "Şimdi Drive'a yedekle"}
          </Button>
        </div>
      )}

      {/* Klasör detayları (Test et sonrası) */}
      {testInfo && testInfo.folder_id && (
        <div className="border rounded-lg p-3 bg-blue-50/40 border-blue-200 space-y-2.5" data-testid="drive-folder-details">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs font-semibold text-blue-900 flex items-center gap-2">
              <FolderOpen size={14} />
              Klasör bilgileri
            </p>
            {testInfo.web_link && (
              <a href={testInfo.web_link} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 underline"
                 data-testid="drive-open-folder">
                <Download size={11} className="rotate-180" />Drive'da aç
              </a>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-[11px]">
            <div className="bg-white border rounded px-2 py-1.5">
              <p className="text-stone-500 uppercase tracking-wide text-[9px] mb-0.5">Yol (breadcrumb)</p>
              <p className="font-mono text-stone-800 break-all" data-testid="drive-folder-path">
                {testInfo.folder_path || testInfo.folder_name}
              </p>
            </div>
            <div className="bg-white border rounded px-2 py-1.5">
              <p className="text-stone-500 uppercase tracking-wide text-[9px] mb-0.5">Klasör ID</p>
              <p className="font-mono text-stone-800 truncate" title={testInfo.folder_id}>
                {testInfo.folder_id}
              </p>
            </div>
            <div className="bg-white border rounded px-2 py-1.5">
              <p className="text-stone-500 uppercase tracking-wide text-[9px] mb-0.5">Klasördeki ZIP sayısı</p>
              <p className="font-medium text-stone-800">
                {testInfo.zip_count ?? 0} dosya · {fmtBytes(testInfo.zip_total_bytes)}
              </p>
            </div>
            <div className="bg-white border rounded px-2 py-1.5">
              <p className="text-stone-500 uppercase tracking-wide text-[9px] mb-0.5">Paylaşım durumu</p>
              <p className="font-medium">
                {testInfo.shared ? <span className="text-emerald-700">✓ Service Account ile paylaşılmış</span>
                                 : <span className="text-amber-700">⚠ Yalnız SA sahibi</span>}
              </p>
            </div>
          </div>
          {testInfo.latest_zip && (
            <div className="bg-white border rounded px-2 py-1.5 text-[11px]">
              <p className="text-stone-500 uppercase tracking-wide text-[9px] mb-0.5">Drive'daki en yeni yedek</p>
              <p className="font-mono text-stone-800 truncate" title={testInfo.latest_zip.name}>
                {testInfo.latest_zip.name}
              </p>
              <p className="text-stone-500 mt-0.5">
                {fmtDate(testInfo.latest_zip.createdTime)} · {fmtBytes(testInfo.latest_zip.size)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Son Drive yüklemesi */}
      {status.has_credentials && (
        <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="drive-stats">
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Son Drive yüklemesi</p>
            <p className={`font-medium ${status.last_backup_drive === "sent" ? "text-emerald-700" : status.last_backup_drive === "failed" ? "text-red-600" : "text-stone-500"}`}>
              {status.last_backup_drive === "sent" ? "Başarılı" : status.last_backup_drive === "failed" ? "Hata" : status.last_backup_drive === "skipped" ? "Atlandı" : "—"}
            </p>
          </div>
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Silinen eski yedek</p>
            <p className="font-medium">{status.last_backup_pruned ?? "—"}</p>
          </div>
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Drive bağlantısı</p>
            <p className="font-medium">{status.last_backup_link ? <a href={status.last_backup_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Drive'da aç</a> : "—"}</p>
          </div>
        </div>
      )}
    </CardCollapse>
  );
}
